import React from 'react';
import { ArrowRight, Clock, CalendarDays } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { BlogPost } from '@/services/blogService';

interface BlogSectionCardProps {
  post: BlogPost;
  index: number;
  isVisible: boolean;
}

const BlogSectionCard: React.FC<BlogSectionCardProps> = ({ 
  post, 
  index, 
  isVisible 
}) => {
  return (
    <Card 
      key={post.id} 
      className={`card transition-all duration-500 hover:shadow-lg flex flex-col h-full ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ 
        transitionDelay: `${(index + 1) * 150}ms`,
      }}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start mb-3">
          <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none transition-colors">
            {post.category}
          </Badge>
          <div className="flex flex-col items-end sm:flex-row sm:items-center text-xs text-muted-foreground gap-1 sm:gap-3">
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {post.date}
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.readingTime}
            </span>
          </div>
        </div>
        <h3 className="text-xl font-bold leading-tight group">
          {post.isPublished ? (
            <Link to={`/blog/${post.id}`} className="hover:text-primary transition-colors">
              {post.title}
            </Link>
          ) : (
            <span className="text-muted-foreground">{post.title}</span>
          )}
        </h3>
      </CardHeader>
      
      <CardContent className="flex-grow">
        <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
          {post.excerpt}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {post.tags.map((tag, tagIndex) => (
            <Badge key={tagIndex} variant="outline" className="text-xs bg-transparent">
              {tag}
            </Badge>
          ))}
        </div>
      </CardContent>

      <CardFooter className="pt-4 border-t border-border/40 mt-auto">
        {post.isPublished ? (
          <Button asChild variant="ghost" className="w-full justify-between hover:bg-primary/5 hover:text-primary group">
            <Link to={`/blog/${post.id}`}>
              Read Article
              <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        ) : (
          <Button variant="ghost" disabled className="w-full justify-between opacity-50 cursor-not-allowed">
            Coming Soon
            <Clock className="h-4 w-4 ml-2" />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default BlogSectionCard;
